import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import api from "../../services/api";

const DESIGNATION_OPTIONS = [
  "Occupational Therapist",
  "Speech Therapist",
  "Physiotherapist",
  "Front Office",
];

const EMPTY_FORM = {
  designation: "",
  place_of_posting: "",
  date_of_joining: "",
  reporting_time: "10:00 AM",
  training_start_date: "",
  training_start_time: "10:00 AM",
  training_location: "",
};

export default function JoiningLetterDialog({ open, user, onClose, onSent }) {
  const branches = useSelector((state) => state.userDetails.detail?.branches) || [];

  const [step, setStep] = useState("form"); // "form" | "preview"
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [html, setHtml] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  // On open: check whether a letter was already sent for this user.
  // If so, jump straight to Preview with the saved content. Otherwise,
  // prefill whatever partial data exists and stay on the form step.
  const handleEnter = async () => {
    if (!user?.id) return;

    setLoadingInitial(true);
    try {
      const response = await api.get(`app/management/users/${user.id}/joining-letter`);
      const data = response?.data || {};

      const prefilledForm = {
        designation: data.designation || "",
        place_of_posting: data.place_of_posting || user?.branches?.[0]?.name || "",
        date_of_joining: data.date_of_joining || "",
        reporting_time: data.reporting_time || "10:00 AM",
        training_start_date: data.training_start_date || "",
        training_start_time: data.training_start_time || "10:00 AM",
        training_location: data.training_location || user?.branches?.[0]?.name || "",
      };
      setForm(prefilledForm);

      if (data.already_sent) {
        setHtml(data.html || "");
        setStep("preview");
      } else {
        setHtml("");
        setStep("form");
      }
    } catch (error) {
      console.error("Failed to load joining letter data:", error);
      setForm({
        ...EMPTY_FORM,
        place_of_posting: user?.branches?.[0]?.name || "",
        training_location: user?.branches?.[0]?.name || "",
      });
      setStep("form");
    } finally {
      setLoadingInitial(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Called from the form step (either first-time preview, or after
  // "Regenerate" sent the admin back here to edit fields). Always forces
  // a fresh render from current field values via regenerate=true, so
  // edited fields actually take effect instead of the saved version
  // silently winning.
  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams({ ...form, regenerate: "true" }).toString();
      const response = await api.get(
        `app/management/users/${user.id}/joining-letter?${params}`
      );
      setHtml(response?.data?.html || "");
      setStep("preview");
    } catch (error) {
      console.error("Failed to load letter preview:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Explicit "start over" action from the preview step — sends the admin
  // back to the form (already prefilled from current `form` state) so
  // they can change values and re-preview.
  const handleRegenerate = () => {
    setStep("form");
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post(`app/management/users/${user.id}/joining-letter`, {
        html,
        ...form,
      });
      onSent();
    } catch (error) {
      console.error("Failed to send joining letter:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: handleEnter }}
      maxWidth={step === "preview" ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>
        {step === "form" ? "Joining Letter — Details" : "Preview & Edit Letter"}
      </DialogTitle>

      <DialogContent dividers>
        {loadingInitial && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loadingInitial && step === "form" && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label="Designation"
                name="designation"
                value={form.designation}
                onChange={handleFormChange}
                size="small"
              >
                {DESIGNATION_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Place of Posting"
                name="place_of_posting"
                value={form.place_of_posting}
                onChange={handleFormChange}
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Joining"
                name="date_of_joining"
                InputLabelProps={{ shrink: true }}
                value={form.date_of_joining}
                onChange={handleFormChange}
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Reporting Time"
                name="reporting_time"
                value={form.reporting_time}
                onChange={handleFormChange}
                size="small"
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                type="date"
                label="Training Start Date"
                name="training_start_date"
                InputLabelProps={{ shrink: true }}
                value={form.training_start_date}
                onChange={handleFormChange}
                size="small"
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Training Start Time"
                name="training_start_time"
                value={form.training_start_time}
                onChange={handleFormChange}
                size="small"
              />
            </Grid>
            <Grid size={4}>
              <TextField
                select
                fullWidth
                label="Training Location"
                name="training_location"
                value={form.training_location}
                onChange={handleFormChange}
                size="small"
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.name}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        {!loadingInitial && step === "preview" && (
          <Box
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => setHtml(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: html }}
            sx={{
              border: "1px solid #E6E9ED",
              borderRadius: "8px",
              padding: "24px",
              minHeight: "500px",
              maxHeight: "60vh",
              overflowY: "auto",
              backgroundColor: "#fff",
              "&:focus": { outline: "none", borderColor: "#4B49AC" },
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!loadingInitial && step === "form" && (
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={previewLoading}
            startIcon={previewLoading ? <CircularProgress size={16} /> : null}
          >
            Preview
          </Button>
        )}
        {!loadingInitial && step === "preview" && (
          <>
            <Button onClick={handleRegenerate} sx={{ textTransform: "none" }}>
              Regenerate
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} /> : null}
            >
              Send Letter
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}